<template>
  <div>
    <Menu />

    <div v-if="loading" class="py-5 text-center">
      <div class="spinner mx-auto"></div>
      <h2 class="mt-3">Loading development...</h2>
    </div>

    <div v-else-if="!developmentFound">
      <div class="container py-5 text-center">
        <h1>Development not found</h1>
        <p>The requested development could not be found.</p>
        <a href="/developments" class="btn btn-all">Back to Developments</a>
      </div>
    </div>

    <div v-else>
      <header class="site-nav">
        <div class="container">
          <div class="site-nav-inner d-flex align-items-center justify-content-between">
            <div class="brand-lockup">
              <a href="/home" class="d-flex align-items-center text-decoration-none">
                <img src="https://www.stratitude.co.za/chas-international/dubai/images/chas-logo.svg" alt="Chas Everitt" class="brand-logo" loading="lazy" />
              </a>

              <span class="brand-divider d-none d-md-inline-block" aria-hidden="true"></span>

              <div class="d-none d-md-block">
                <a href="https://www.leadingre.com/" class="d-flex align-items-center text-decoration-none" target="_blank" rel="noopener noreferrer">
                  <img src="https://www.stratitude.co.za/chas-international/dubai/images/leading-logo1.png" alt="Leading Real Estate" class="partner-logo" loading="lazy" />
                </a>
              </div>
            </div>

            <nav class="d-none d-lg-flex site-nav-links align-items-center">
              <a class="nav-link" href="/properties">Buy</a>
              <a class="nav-link" href="/sell">Sell</a>
              <a class="nav-link" href="/developments">Developments</a>
              <a class="nav-link" href="/commercial">Commercial</a>
            </nav>

            <button class="btn hamburger d-lg-none" type="button" data-bs-toggle="offcanvas" data-bs-target="#mobileNav" aria-label="Open menu">
              <i class="bi bi-list" style="font-size:1.25rem;"></i>
            </button>
          </div>
        </div>
      </header>

      <section class="property-hero d-flex align-items-end">
        <button type="button" class="hero-image-button" @click="openGallery(0)" aria-label="Open gallery">
          <img :src="featuredImage" class="hero-image" :alt="cleanText(development.title)" />
        </button>

        <div class="hero-overlay"></div>

        <div class="container pb-4 hero-content">
          <div class="property-kicker">
            Development
          </div>

          <h1 class="property-title mt-2 text-uppercase">
            <strong>{{ cleanText(development.title) }}</strong>
          </h1>

          <div class="property-sub">
            {{ priceRange }}
            <span v-if="hasConvertedPriceRange">
              • {{ convertedPriceRangeZAR }} / {{ convertedPriceRangeUSD }}
            </span>
            <span v-if="developmentLocation">• {{ developmentLocation }}</span>
          </div>
        </div>
      </section>

      <section class="property-summary">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center gap-2 justify-content-center justify-content-md-between">
            <div class="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
              <div class="summary-chip" v-if="development.ref_id">
                Ref <strong>{{ development.ref_id }}</strong>
              </div>

              <div class="summary-chip" v-if="development.site">
                Site <strong>{{ development.site }}</strong>
              </div>

              <div class="summary-chip" v-if="galleryImages.length > 0">
                Images <strong>{{ galleryImages.length }}</strong>
              </div>

              <div class="summary-chip" v-if="availableProperties.length > 0">
                Units <strong>{{ availableProperties.length }}</strong>
              </div>
            </div>

            <button type="button" class="btn btn-gallery ms-md-auto" @click="openGallery(0)" v-if="galleryImages.length > 0">
              View Gallery
            </button>
          </div>
        </div>
      </section>

      <section class="py-5 bg-white">
        <div class="container">
          <div class="row g-4">
            <div class="col-12 col-lg-8">
              <div class="section-title">About Development</div>
              <div class="section-rule"></div>

              <div class="paragraph property-description" v-html="formattedDescription"></div>

              <div class="development-info-panel mt-5">
                <div class="row g-4 align-items-start">
                  <div class="col-12 col-xl-6">
                    <h3 class="development-panel-title">Development details</h3>

                    <div class="reg-row">
                      <strong>Development</strong>
                      <span>{{ cleanText(development.title) }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Reference</strong>
                      <span>{{ development.ref_id || meta.ref_id || '—' }}</span>
                    </div>

                    <div class="reg-row" v-if="development.slug">
                      <strong>Slug</strong>
                      <span>{{ development.slug }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Starting From</strong>
                      <span>
                        {{ lowPriceDisplay }}
                        <template v-if="convertedLowPriceZAR && convertedLowPriceUSD">
                          <br>
                          <small>Approx. {{ convertedLowPriceZAR }}</small>
                          <br>
                          <small>Approx. {{ convertedLowPriceUSD }}</small>
                        </template>
                      </span>
                    </div>

                    <div class="reg-row">
                      <strong>Highest Price</strong>
                      <span>
                        {{ highPriceDisplay }}
                        <template v-if="convertedHighPriceZAR && convertedHighPriceUSD">
                          <br>
                          <small>Approx. {{ convertedHighPriceZAR }}</small>
                          <br>
                          <small>Approx. {{ convertedHighPriceUSD }}</small>
                        </template>
                      </span>
                    </div>
                  </div>

                  <div class="col-12 col-xl-6">
                    <h3 class="development-panel-title">Contact information</h3>

                    <div class="reg-row">
                      <strong>Contact Name</strong>
                      <span>{{ development.contact_name || 'Chas Everitt International' }}</span>
                    </div>

                    <div class="reg-row" v-if="development.contact_email">
                      <strong>Email</strong>
                      <span>{{ development.contact_email }}</span>
                    </div>

                    <div class="reg-row" v-if="development.contact_phone">
                      <strong>Phone</strong>
                      <span>{{ development.contact_phone }}</span>
                    </div>

                    <div class="reg-row">
                      <strong>Agency name</strong>
                      <span>Chas Everitt International</span>
                    </div>

                    <div class="reg-row" v-if="meta.updated_at">
                      <strong>Updated</strong>
                      <span>{{ formatDate(meta.updated_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="amenitiesList.length > 0">
                <div class="section-title">Amenities</div>
                <div class="section-rule"></div>

                <div class="row g-2">
                  <div class="col-12 col-md-6" v-for="amenity in amenitiesList" :key="amenity">
                    <div class="feature-item">
                      <i class="bi bi-check-circle me-2"></i>{{ amenity }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="activitiesList.length > 0">
                <div class="section-title">Activities</div>
                <div class="section-rule"></div>

                <div class="row g-2">
                  <div class="col-12 col-md-6" v-for="activity in activitiesList" :key="activity">
                    <div class="feature-item">
                      <i class="bi bi-check-circle me-2"></i>{{ activity }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="availableProperties.length > 0">
                <div class="section-title">Available Properties</div>
                <div class="section-rule"></div>

                <div class="row g-4">
                  <div class="col-12 col-md-6" v-for="(developmentProperty, index) in availableProperties" :key="'development-property-' + index">
                    <div class="development-property-card h-100">
                      <img v-if="developmentProperty.image" :src="getImageUrl(developmentProperty.image)" class="development-property-img" :alt="'Development property ' + (index + 1)" />

                      <div class="development-property-body">
                        <h4>{{ developmentProperty.title || 'Property ' + (index + 1) }}</h4>

                        <p v-if="developmentProperty.description">
                          {{ cleanText(developmentProperty.description) }}
                        </p>

                        <div class="development-property-price" v-if="developmentProperty.from_price">
                          <div>From {{ formatCurrency(developmentProperty.from_price) }}</div>

                          <template v-if="currencyRates">
                            <div class="converted-development-property-price">
                              Approx. {{ convertedSinglePrice(developmentProperty.from_price, 'ZAR') }}
                            </div>
                            <div class="converted-development-property-price">
                              Approx. {{ convertedSinglePrice(developmentProperty.from_price, 'USD') }}
                            </div>
                          </template>
                        </div>

                        <div class="row g-2 mt-3">
                          <div class="col-6" v-if="developmentProperty.erf_size">
                            <div class="mini-detail">
                              {{ developmentProperty.erf_size }}
                              <span>ERF SIZE</span>
                            </div>
                          </div>

                          <div class="col-6" v-if="developmentProperty.floor_size">
                            <div class="mini-detail">
                              {{ developmentProperty.floor_size }}
                              <span>FLOOR SIZE</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-5" v-if="seoKeywordsList.length > 0">
                <div class="section-title">Related Keywords</div>
                <div class="section-rule"></div>

                <div class="keyword-list">
                  <span v-for="keyword in seoKeywordsList" :key="keyword">
                    {{ keyword }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="property-box">
                <div class="ref-label">Price Range</div>

                <div class="property-price">
                  <div>{{ priceRange }}</div>

                  <template v-if="hasConvertedPriceRange">
                    <div class="converted-property-price">
                      Approx. {{ convertedPriceRangeZAR }}
                    </div>
                    <div class="converted-property-price">
                      Approx. {{ convertedPriceRangeUSD }}
                    </div>
                  </template>
                </div>

                <div class="ref-label">Reference</div>
                <p class="mb-0 fw-bold">
                  {{ development.ref_id || meta.ref_id || '—' }}
                </p>

                <div class="ref-label mt-3">Location</div>
                <p class="mb-0">{{ developmentLocation }}</p>

                <div class="d-grid gap-2 mt-3">
                  <a href="#development-contact" class="btn btn-all">Contact Us</a>
                  <button type="button" class="btn btn-outline-brand" @click="printToPDF">
                    Print Development
                  </button>
                </div>
              </div>

              <div id="development-contact" class="agent-list-card mt-4">
                <div class="section-title">Contact Details</div>
                <div class="section-rule"></div>

                <div class="agent-list-item">
                  <div class="agent-list-photo agent-list-photo-placeholder">
                    <i class="bi bi-building"></i>
                  </div>

                  <div class="agent-list-content">
                    <p class="agent-name">
                      {{ development.contact_name || 'Chas Everitt International' }}
                    </p>

                    <p class="agent-role">Development Sales</p>

                    <div class="agent-contact-list" v-if="contactRequested">
                      <a v-if="development.contact_phone" :href="'tel:' + development.contact_phone">
                        <i class="bi bi-telephone me-2"></i>{{ development.contact_phone }}
                      </a>

                      <a v-if="development.contact_email" :href="'mailto:' + development.contact_email">
                        <i class="bi bi-envelope me-2"></i>{{ development.contact_email }}
                      </a>

                      <a v-if="whatsappLink" :href="whatsappLink" target="_blank" rel="noopener">
                        <i class="bi bi-whatsapp me-2"></i>WhatsApp
                      </a>
                    </div>

                    <div class="agent-actions mt-3">
                      <button v-if="!contactRequested" class="btn btn-all btn-sm" type="button" @click="contactRequested = true">
                        Details
                      </button>

                      <a v-else-if="whatsappLink" class="btn btn-all btn-sm" :href="whatsappLink" target="_blank" rel="noopener">
                        Enquire Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div class="property-box mt-4">
                <div class="section-title">Share</div>
                <div class="section-rule"></div>

                <div class="d-flex gap-2 flex-wrap">
                  <a class="btn btn-outline-brand" :href="facebookShareLink" target="_blank" rel="noopener">
                    Facebook
                  </a>

                  <a class="btn btn-outline-brand" :href="pinterestShareLink" target="_blank" rel="noopener">
                    Pinterest
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Gallery Modal -->
      <div class="modal fade" id="developmentGalleryModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-fullscreen modal-dialog-centered">
          <div class="modal-content gallery-modal">
            <div class="modal-header">
              <h5 class="modal-title">
                Gallery
                <span v-if="galleryImages.length > 0">
                  {{ activeGalleryIndex + 1 }} / {{ galleryImages.length }}
                </span>
              </h5>

              <div class="btn-group ms-auto me-2" role="group" aria-label="Gallery view toggle">
                <button type="button" class="btn btn-outline-secondary btn-sm" :class="{ active: galleryView == 'single' }" @click="showSingle">
                  Single
                </button>

                <button type="button" class="btn btn-outline-secondary btn-sm" :class="{ active: galleryView == 'grid' }" @click="showGrid">
                  Grid
                </button>
              </div>

              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="modal-body">
              <div v-if="galleryView == 'single'" class="gallery-single-wrap">
                <button class="gallery-nav gallery-nav-prev" type="button" @click="prevImage">
                  <i class="bi bi-chevron-left"></i>
                </button>

                <img :src="galleryImages[activeGalleryIndex]" class="gallery-full-img" :alt="'Development image ' + (activeGalleryIndex + 1)" />

                <button class="gallery-nav gallery-nav-next" type="button" @click="nextImage">
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>

              <div v-if="galleryView == 'grid'" class="container-fluid">
                <div class="row g-3">
                  <div class="col-6 col-md-4 col-lg-3" v-for="(img, idx) in galleryImages" :key="'thumb-' + idx">
                    <button class="thumb-btn" type="button" @click="thumbClick(idx)">
                      <img class="thumb-img" :src="img" :alt="'Thumbnail ' + (idx + 1)">
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer gallery-thumbs" v-if="galleryView == 'single'">
              <button class="gallery-thumb-small" type="button" v-for="(img, idx) in galleryImages" :key="'small-thumb-' + idx" :class="{ active: activeGalleryIndex == idx }" @click="activeGalleryIndex = idx">
                <img :src="img" :alt="'Thumbnail ' + (idx + 1)">
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="offcanvas offcanvas-end offcanvas-custom" tabindex="-1" id="mobileNav" aria-labelledby="mobileNavLabel">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title text-white" id="mobileNavLabel">Menu</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div class="offcanvas-body">
          <div class="list-group list-group-flush">
            <a class="list-group-item list-group-item-action" href="/properties">Buy</a>
            <a class="list-group-item list-group-item-action" href="/sell">Sell</a>
            <a class="list-group-item list-group-item-action" href="/developments">Developments</a>
            <a class="list-group-item list-group-item-action" href="/commercial">Commercial</a>
          </div>
          <hr />
        </div>
      </div>

      <Footer />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/Menu.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'DevelopmentProperty',

  components: {
    Menu,
    Footer
  },

  data() {
    return {
      loading: true,
      developmentFound: false,
      development: {},
      currencyRates: null,
      meta: {},
      allDevelopments: [],
      imageBaseUrl: 'https://luxury-dev.chaseveritt.co.za/',
      contactRequested: false,
      galleryView: 'single',
      activeGalleryIndex: 0,
      galleryModalInstance: null
    }
  },

  created() {
    this.fetchCurrencyRates()
    this.fetchDevelopment()
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  computed: {
    developmentId() {
      if (this.$route && this.$route.params && this.$route.params.propertyId) {
        return this.$route.params.propertyId
      }

      if (this.$route && this.$route.params && this.$route.params.developmentId) {
        return this.$route.params.developmentId
      }

      if (this.$route && this.$route.params && this.$route.params.slug) {
        return this.$route.params.slug
      }

      if (this.$route && this.$route.params && this.$route.params.ref_id) {
        return this.$route.params.ref_id
      }

      if (this.$route && this.$route.params && this.$route.params.id) {
        return this.$route.params.id
      }

      return ''
    },

    galleryImages() {
      const images = []

      if (this.development && this.development.cover_image) {
        images.push(this.getImageUrl(this.development.cover_image))
      }

      if (this.development && this.development.image_gallery && this.development.image_gallery.length) {
        this.development.image_gallery.forEach((image) => {
          if (image && image.url) {
            const imageUrl = this.getImageUrl(image.url)

            if (images.indexOf(imageUrl) === -1) {
              images.push(imageUrl)
            }
          }
        })
      }

      return images
    },

    featuredImage() {
      if (this.galleryImages.length > 0) {
        return this.galleryImages[0]
      }

      return 'https://www.stratitude.co.za/chas-international/dubai/images/placeholder.jpg'
    },

    availableProperties() {
      if (!this.development || !this.development.properties || !this.development.properties.length) {
        return []
      }

      return this.development.properties.filter((item) => {
        if (!item) {
          return false
        }

        if (item.description) {
          return true
        }

        if (item.from_price) {
          return true
        }

        if (item.erf_size) {
          return true
        }

        if (item.floor_size) {
          return true
        }

        if (item.image) {
          return true
        }

        return false
      })
    },

    formattedDescription() {
      if (!this.development.description) {
        return ''
      }

      return this.cleanText(this.development.description).replace(/\n/g, '<br>')
    },

    amenitiesList() {
      return this.textToList(this.development.amenities)
    },

    activitiesList() {
      return this.textToList(this.development.activities)
    },

    seoKeywordsList() {
      if (!this.development.seo_keywords) {
        return []
      }

      return this.development.seo_keywords
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '')
    },

    priceRange() {
      if (this.hasPriceRange) {
        return this.formatCurrency(this.development.low_price) + ' - ' + this.formatCurrency(this.development.high_price)
      }

      if (this.development.low_price) {
        return 'From ' + this.formatCurrency(this.development.low_price)
      }

      return 'POA'
    },

    hasConvertedPriceRange() {
      if (!this.currencyRates) {
        return false
      }

      const low = this.priceNumber(this.development.low_price)
      const high = this.priceNumber(this.development.high_price)

      if (low > 0 || high > 0) {
        return true
      }

      return false
    },

    convertedPriceRangeZAR() {
      return this.convertedPriceRange('ZAR')
    },

    convertedPriceRangeUSD() {
      return this.convertedPriceRange('USD')
    },

    convertedLowPriceZAR() {
      return this.convertedSinglePrice(this.development.low_price, 'ZAR')
    },

    convertedLowPriceUSD() {
      return this.convertedSinglePrice(this.development.low_price, 'USD')
    },

    convertedHighPriceZAR() {
      return this.convertedSinglePrice(this.development.high_price, 'ZAR')
    },

    convertedHighPriceUSD() {
      return this.convertedSinglePrice(this.development.high_price, 'USD')
    },

    hasPriceRange() {
      if (this.development.low_price && this.development.high_price) {
        return true
      }

      return false
    },

    lowPriceDisplay() {
      if (this.development.low_price) {
        return this.formatCurrency(this.development.low_price)
      }

      return 'POA'
    },

    highPriceDisplay() {
      if (this.development.high_price) {
        return this.formatCurrency(this.development.high_price)
      }

      return 'POA'
    },

    developmentLocation() {
      return this.development.location ||
        this.development.loc_region ||
        this.development.loc_suburb ||
        this.development.city ||
        this.development.area ||
        'Dubai'
    },

    currentUrl() {
      return encodeURIComponent(window.location.href)
    },

    facebookShareLink() {
      return 'https://www.facebook.com/sharer/sharer.php?u=' + this.currentUrl
    },

    pinterestShareLink() {
      const title = encodeURIComponent(this.development.title || 'Dubai Development')
      const image = encodeURIComponent(this.featuredImage)

      return 'https://www.pinterest.com/pin/create/button/?url=' + this.currentUrl + '&media=' + image + '&description=' + title
    },

    whatsappLink() {
      if (!this.development.contact_phone) {
        return ''
      }

      const currentUrl = window.location.href
      const cleanedNumber = this.development.contact_phone.replace(/\D/g, '')
      const formattedNumber = this.normalisePhoneNumber(cleanedNumber)
      const message = encodeURIComponent('Hello! I am interested in the development I found on your website: ' + currentUrl)

      return 'https://wa.me/' + formattedNumber + '?text=' + message
    }
  },

  methods: {
    async fetchCurrencyRates() {
      try {
        const response = await axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-currency-rates.php'
        )

        if (!response.data || response.data.success !== true) {
          return
        }

        this.currencyRates = {
          aedToZar: response.data.aedToZar,
          aedToUsd: response.data.aedToUsd,
          updatedAt: response.data.updatedAt,
          cachedAt: response.data.cachedAt
        }
      } catch (error) {
        console.log(error)
        this.currencyRates = null
      }
    },

    priceNumber(value) {
      if (!value) {
        return 0
      }

      if (typeof value === 'number') {
        if (Number.isNaN(value)) {
          return 0
        }

        return value
      }

      const cleanedValue = this.cleanText(value)
        .replace(/,/g, '')
        .replace(/[^0-9.-]+/g, '')

      const numberValue = Number(cleanedValue)

      if (Number.isNaN(numberValue)) {
        return 0
      }

      return numberValue
    },

    convertedSinglePrice(value, currency) {
      const amount = this.priceNumber(value)

      if (amount == 0 || !this.currencyRates) {
        return ''
      }

      if (currency == 'ZAR') {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: 'ZAR',
          maximumFractionDigits: 0
        }).format(amount * this.currencyRates.aedToZar)
      }

      if (currency == 'USD') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0
        }).format(amount * this.currencyRates.aedToUsd)
      }

      return ''
    },

    convertedPriceRange(currency) {
      if (!this.hasConvertedPriceRange) {
        return ''
      }

      const low = this.priceNumber(this.development.low_price)
      const high = this.priceNumber(this.development.high_price)

      if (low > 0 && high > 0) {
        return this.convertedSinglePrice(low, currency) + ' - ' + this.convertedSinglePrice(high, currency)
      }

      if (low > 0) {
        return 'From ' + this.convertedSinglePrice(low, currency)
      }

      if (high > 0) {
        return 'Up to ' + this.convertedSinglePrice(high, currency)
      }

      return ''
    },

    async fetchDevelopment() {
      this.loading = true
      this.developmentFound = false
      this.development = {}
      this.meta = {}

      try {
        const response = await axios.get(
          'https://chas-dubai.stratitude-develop.co.za/api/fetch-developments.php',
          {
            headers: {
              Accept: 'application/json'
            }
          }
        )

        if (
          response.data &&
          response.data.success === true &&
          Array.isArray(response.data.developments)
        ) {
          this.allDevelopments = response.data.developments
          this.findCurrentDevelopment()
        }
      } catch (error) {
        console.log('Development fetch error:', error)
      }

      this.loading = false
    },

    findCurrentDevelopment() {
      let matched = null
      const current = this.safeString(this.developmentId).toLowerCase()

      this.allDevelopments.forEach((item) => {
        const row = this.getDevelopmentRow(item)
        const meta = item && item.meta ? item.meta : {}

        if (!row || !row.title) {
          return
        }

        const rowRef = this.safeString(row.ref_id).toLowerCase()
        const metaRef = this.safeString(meta.ref_id).toLowerCase()
        const rowSlug = this.safeString(row.slug).toLowerCase()
        const generatedSlug = this.slugify(row.title).toLowerCase()

        if (!matched && rowRef === current) {
          matched = {
            development: row,
            meta: meta
          }
        }

        if (!matched && metaRef === current) {
          matched = {
            development: row,
            meta: meta
          }
        }

        if (!matched && rowSlug === current) {
          matched = {
            development: row,
            meta: meta
          }
        }

        if (!matched && generatedSlug === current) {
          matched = {
            development: row,
            meta: meta
          }
        }
      })

      if (matched) {
        this.development = matched.development
        this.meta = matched.meta
        this.developmentFound = true
        this.setPageMeta()
      }
    },

    getDevelopmentRow(item) {
      if (
        item &&
        item.development &&
        item.development.data &&
        item.development.data.development &&
        item.development.data.development[0]
      ) {
        return item.development.data.development[0]
      }

      if (item && item.development && item.development.title) {
        return item.development
      }

      if (item && item.title) {
        return item
      }

      return {}
    },

    getImageUrl(path) {
      if (!path) {
        return 'https://www.stratitude.co.za/chas-international/dubai/images/placeholder.jpg'
      }

      if (path.indexOf('http') === 0) {
        return path
      }

      return this.imageBaseUrl.replace(/\/$/, '') + '/' + path.replace(/^\//, '')
    },

    textToList(value) {
      if (!value) {
        return []
      }

      const cleaned = this.cleanText(value)

      if (cleaned.indexOf('\n') !== -1) {
        return cleaned
          .split('\n')
          .map((item) => item.replace(/^\*\s*/, '').trim())
          .filter((item) => item !== '')
      }

      return cleaned
        .split('. ')
        .map((item) => item.trim())
        .filter((item) => item !== '')
    },

    openGallery(index) {
      if (this.galleryImages.length === 0) {
        return
      }

      this.galleryView = 'single'
      this.activeGalleryIndex = index

      this.$nextTick(() => {
        const modalEl = document.getElementById('developmentGalleryModal')

        if (modalEl && window.bootstrap) {
          this.galleryModalInstance = window.bootstrap.Modal.getOrCreateInstance(modalEl)
          this.galleryModalInstance.show()
        }
      })
    },

    showSingle() {
      this.galleryView = 'single'
    },

    showGrid() {
      this.galleryView = 'grid'
    },

    thumbClick(index) {
      this.activeGalleryIndex = index
      this.galleryView = 'single'
    },

    nextImage() {
      if (this.galleryImages.length === 0) {
        return
      }

      if (this.activeGalleryIndex >= this.galleryImages.length - 1) {
        this.activeGalleryIndex = 0
      } else {
        this.activeGalleryIndex += 1
      }
    },

    prevImage() {
      if (this.galleryImages.length === 0) {
        return
      }

      if (this.activeGalleryIndex <= 0) {
        this.activeGalleryIndex = this.galleryImages.length - 1
      } else {
        this.activeGalleryIndex -= 1
      }
    },

    handleKeydown(event) {
      const modalEl = document.getElementById('developmentGalleryModal')

      if (!modalEl) {
        return
      }

      if (!modalEl.classList.contains('show')) {
        return
      }

      if (event.key === 'ArrowRight') {
        this.nextImage()
      }

      if (event.key === 'ArrowLeft') {
        this.prevImage()
      }

      if (event.key === 'Escape' && this.galleryModalInstance) {
        this.galleryModalInstance.hide()
      }
    },

    formatCurrency(value) {
      if (!value) {
        return ''
      }

      const original = this.cleanText(value).toUpperCase()

      if (original.indexOf('AED') !== -1) {
        return this.cleanText(value)
      }

      const numberValue = Number(String(value).replace(/[^0-9.-]+/g, ''))

      if (Number.isNaN(numberValue)) {
        return this.cleanText(value)
      }

      return new Intl.NumberFormat('en-AE', {
        style: 'currency',
        currency: 'AED',
        maximumFractionDigits: 0
      }).format(numberValue)
    },

    normalisePhoneNumber(number) {
      if (!number) {
        return ''
      }

      if (number.startsWith('971')) {
        return number
      }

      if (number.startsWith('27')) {
        return number
      }

      if (number.charAt(0) === '0') {
        return '971' + number.substring(1)
      }

      return '971' + number
    },

    printToPDF() {
      window.print()
    },

    safeString(value) {
      if (!value) {
        return ''
      }

      return value.toString()
    },

    slugify(value) {
      if (!value) {
        return 'development'
      }

      return value
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
    },

    formatDate(value) {
      if (!value) {
        return '—'
      }

      const date = new Date(value)

      if (isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleDateString('en-AE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    cleanText(value) {
      if (!value) {
        return ''
      }

      return value
        .toString()
        .replace(/ΓÇö/g, '—')
        .replace(/ΓÇô/g, '–')
        .replace(/ΓÇÖ/g, '’')
        .replace(/ΓÇ£/g, '“')
        .replace(/ΓÇ¥/g, '”')
        .replace(/ΓÇó/g, '•')
        .replace(/Γ£ö∩╕Å/g, '✓')
        .replace(/≡ƒôì/g, '📍')
        .replace(/ΓÇª/g, '…')
        .replace(/┬á/g, ' ')
        .replace(/&amp;/g, '&')
    },

    setPageMeta() {
      document.title = this.development.seo_meta || this.development.title || 'Dubai Development'

      const description = this.cleanText(this.development.description || '')
      const image = this.featuredImage

      this.setMetaTag('description', description)
      this.setMetaTag('keywords', this.development.seo_keywords || '')
      this.setOgTag('og:title', this.development.title || 'Dubai Development')
      this.setOgTag('og:description', description)
      this.setOgTag('og:image', image)
      this.setOgTag('og:url', window.location.href)
    },

    setMetaTag(name, content) {
      let tag = document.querySelector('meta[name="' + name + '"]')

      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', name)
        document.head.appendChild(tag)
      }

      tag.setAttribute('content', content)
    },

    setOgTag(property, content) {
      let tag = document.querySelector('meta[property="' + property + '"]')

      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('property', property)
        document.head.appendChild(tag)
      }

      tag.setAttribute('content', content)
    }
  }
}
</script>

<style scoped>
.spinner {
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-left: 8px solid #255699;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1.2s linear infinite;
  margin-top: 80px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.property-hero {
  position: relative;
  min-height: 600px;
  overflow: hidden;
  background: #111;
}

.hero-image-button {
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  cursor: zoom-in;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, .65), rgba(0, 0, 0, .1));
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
  color: #fff;
}

.property-kicker {
  display: inline-block;
  background: #255699;
  color: #fff;
  padding: 7px 16px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
}

.property-title {
  font-size: clamp(30px, 5vw, 64px);
  line-height: 1;
}

.property-sub {
  font-size: 18px;
  font-weight: 600;
}

.property-summary {
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 18px 0;
}

.summary-chip {
  background: #f7f7f7;
  padding: 10px 18px;
  font-size: 14px;
  color: #000;
}

.btn-gallery,
.btn-all {
  background: #255699;
  color: #fff;
  border: 1px solid #255699;
}

.btn-gallery:hover,
.btn-all:hover {
  background: #1c4379;
  color: #fff;
}

.btn-outline-brand {
  border: 1px solid #255699;
  color: #255699;
  background: #fff;
}

.btn-outline-brand:hover {
  background: #255699;
  color: #fff;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  color: #333;
}

.section-rule {
  width: 70px;
  height: 4px;
  background: #255699;
  margin: 12px 0 24px;
}

.paragraph {
  color: #444;
  font-size: 16px;
  line-height: 1.8;
}

.property-box,
.agent-list-card,
.development-info-panel {
  background: #f7f7f7;
  border-radius: 18px;
  padding: 24px;
}

.ref-label {
  color: #666;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.property-price {
  font-size: 30px;
  font-weight: 800;
  color: #255699;
}

.converted-property-price {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.75;
  line-height: 1.4;
}

.converted-development-property-price {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.75;
  line-height: 1.35;
}

.development-panel-title {
  font-size: 24px;
  font-weight: 800;
  color: #333;
  margin-bottom: 24px;
}

.reg-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 18px;
  margin-bottom: 18px;
  align-items: center;
  color: #444;
}

.reg-row strong {
  font-size: 16px;
  font-weight: 800;
}

.reg-row span {
  font-size: 16px;
  overflow-wrap: anywhere;
}

.feature-item {
  background: #f7f7f7;
  border-radius: 12px;
  padding: 14px 16px;
  color: #333;
}

.development-property-card {
  background: #f7f7f7;
  border-radius: 18px;
  overflow: hidden;
}

.development-property-img {
  width: 100%;
  height: 260px;
  object-fit: cover;
}

.development-property-body {
  padding: 20px;
}

.development-property-body h4 {
  font-size: 20px;
  font-weight: 800;
}

.development-property-price {
  color: #255699;
  font-size: 18px;
  font-weight: 800;
}

.mini-detail {
  background: #fff;
  padding: 12px;
  font-size: 18px;
  font-weight: 800;
}

.mini-detail span {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #666;
  margin-top: 4px;
}

.keyword-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.keyword-list span {
  background: #255699;
  color: #fff;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
}

.agent-list-item {
  display: flex;
  gap: 16px;
  padding: 18px 0;
}

.agent-list-photo {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  overflow: hidden;
  background: #e8e8e8;
  flex: 0 0 96px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-list-photo-placeholder {
  font-size: 42px;
  color: #777;
}

.agent-list-content {
  flex: 1;
  min-width: 0;
}

.agent-name {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 2px;
  color: #333;
}

.agent-role {
  margin-bottom: 8px;
  color: #666;
}

.agent-contact-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 10px;
}

.agent-contact-list a {
  color: #255699;
  text-decoration: none;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.agent-actions .btn-sm {
  padding: 7px 18px;
  font-size: 13px;
}

.gallery-modal {
  background: #111;
  color: #fff;
}

.gallery-modal .modal-header,
.gallery-modal .modal-footer {
  border-color: rgba(255, 255, 255, .15);
}

.gallery-modal .btn-close {
  filter: invert(1);
}

.gallery-single-wrap {
  position: relative;
  min-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-full-img {
  max-width: 100%;
  max-height: calc(100vh - 220px);
  object-fit: contain;
}

.gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  border: 0;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .2);
  color: #fff;
  font-size: 28px;
}

.gallery-nav-prev {
  left: 20px;
}

.gallery-nav-next {
  right: 20px;
}

.thumb-btn {
  display: block;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: zoom-in;
}

.thumb-img {
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 10px;
}

.gallery-thumbs {
  overflow-x: auto;
  flex-wrap: nowrap;
  justify-content: flex-start;
}

.gallery-thumb-small {
  border: 2px solid transparent;
  padding: 0;
  background: transparent;
  width: 90px;
  height: 60px;
  flex: 0 0 auto;
  opacity: .65;
}

.gallery-thumb-small.active {
  border-color: #fff;
  opacity: 1;
}

.gallery-thumb-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 991px) {
  .property-hero {
    min-height: 420px;
  }

  .reg-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

@media (max-width: 575px) {
  .agent-list-item {
    align-items: flex-start;
  }

  .agent-list-photo {
    width: 76px;
    height: 76px;
    flex-basis: 76px;
  }
}

.gallery-modal {
  background: #111;
  color: #fff;
  height: 100vh;
}

.gallery-modal .modal-body {
  overflow-y: auto;
  min-height: 0;
}

.gallery-modal .modal-content {
  height: 100vh;
}
</style>